import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthenticatedFetch from './useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

// Default query options (similar to React Query defaults)
const DEFAULT_QUERY_OPTIONS = {
  enabled: false,
  retry: 3,
  staleTime: 5 * 60 * 1000, // 5 minutes
};

const normalizeBaseUrl = (raw) => {
  if (!raw) return '';
  return String(raw).trim().replace(/\/+$/, '');
};

const ensureApiPrefix = (path) => {
  const p = String(path || '');
  if (!p.startsWith('/')) return `/api/${p}`;
  return p.startsWith('/api/') || p === '/api' ? p : `/api${p}`;
};

const buildApiUrl = (endpoint) => {
  const base = normalizeBaseUrl(API_CONFIG.BASE_URL);
  const path = ensureApiPrefix(endpoint);

  // Allow BASE_URL to be either:
  // - a full origin (e.g. "http://localhost:3001" or "http://localhost:3001/api")
  // - a proxy path (e.g. "/api")
  if (!base) return path;

  // If BASE_URL already ends with "/api", avoid doubling.
  const baseNoApi = base.endsWith('/api') ? base.slice(0, -4) : base;

  if (base.startsWith('/')) {
    // If someone sets BASE_URL to "/api", avoid doubling it.
    if (base === '/api' && path.startsWith('/api')) return path;
    return `${baseNoApi}${path}`;
  }
  return `${baseNoApi}${path}`;
};

// API request helper
const apiRequest = async (authenticatedFetch, endpoint) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestJson = async (authenticatedFetch, endpoint, body) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, {
    method: 'POST',
    body: JSON.stringify(body || {}),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestJsonWithMethod = async (authenticatedFetch, endpoint, method, body) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, {
    method,
    body: JSON.stringify(body || {}),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

const apiRequestWithMethod = async (authenticatedFetch, endpoint, method) => {
  const fullUrl = buildApiUrl(endpoint);
  const response = await authenticatedFetch(fullUrl, { method });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const invalidateTaskOrderSpendingQueries = async (queryClient) => {
  await queryClient.invalidateQueries({ queryKey: ['taskOrderTaskSpendingByMonth'] });
  await queryClient.invalidateQueries({ queryKey: ['taskOrderActualDollarsByTask'] });
};


// Test API query hook
export const useTestApi = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['testApi'],
    queryFn: () => apiRequest(authenticatedFetch, '/test'),
    ...DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};

export const useProjects = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projects'],
    queryFn: () => apiRequest(authenticatedFetch, '/projects'),
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useTrashedProjects = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['trashedProjects'],
    queryFn: () => apiRequest(authenticatedFetch, '/projects/trash'),
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useCreateProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/projects', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUploadProjectPhoto = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) => {
      const pid = Number(projectId);
      return apiRequestJson(authenticatedFetch, `/projects/${pid}/photo`, payload);
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', pid] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteProjectPhoto = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId }) => {
      const pid = Number(projectId);
      return apiRequestWithMethod(authenticatedFetch, `/projects/${pid}/photo`, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', pid] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useTaskOrders = (projectIdOrOptions = {}, maybeOptions = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const projectId = Number.isInteger(projectIdOrOptions) ? projectIdOrOptions : null;
  const options = Number.isInteger(projectIdOrOptions) ? (maybeOptions || {}) : (projectIdOrOptions || {});
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrders', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/task-orders`),
    enabled: optionsEnabled ?? Number.isInteger(projectId),
    staleTime: 30_000,
    ...restOptions,
  });
};

export const useProjectMilestones = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const enabled = Number.isInteger(projectId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['milestones', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/milestones`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useCreateMilestone = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) => {
      const pid = Number(projectId);
      return apiRequestJson(authenticatedFetch, `/projects/${pid}/milestones`, payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteMilestone = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, milestoneId }) => {
      const pid = Number(projectId);
      return apiRequestWithMethod(authenticatedFetch, `/projects/${pid}/milestones/${milestoneId}`, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateMilestone = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, milestoneId, payload }) => {
      const pid = Number(projectId);
      const mid = Number(milestoneId);
      return apiRequestJsonWithMethod(authenticatedFetch, `/projects/${pid}/milestones/${mid}`, 'PUT', payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useCreateTaskOrder = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) => {
      const pid = Number(projectId);
      return apiRequestJson(authenticatedFetch, `/projects/${pid}/task-orders`, payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders', pid] });
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders'] });
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateTaskOrder = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, payload }) => {
      const pid = Number(projectId);
      const toid = Number(taskOrderId);
      return apiRequestJsonWithMethod(authenticatedFetch, `/projects/${pid}/task-orders/${toid}`, 'PUT', payload);
    },
    onSuccess: async (data, variables, context) => {
      const pid = Number(variables?.projectId);
      if (Number.isInteger(pid)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders', pid] });
        await queryClient.invalidateQueries({ queryKey: ['milestones', pid] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrders'] });
        await queryClient.invalidateQueries({ queryKey: ['milestones'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useTasks = (projectIdOrParamsOrOptions = {}, maybeOptions = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  // Back-compat: useTasks(projectId, options)
  // New: useTasks({ projectId, taskOrderId }, options)
  const params =
    Number.isInteger(projectIdOrParamsOrOptions) || projectIdOrParamsOrOptions === null
      ? { projectId: Number.isInteger(projectIdOrParamsOrOptions) ? projectIdOrParamsOrOptions : null, taskOrderId: null }
      : (projectIdOrParamsOrOptions || {});

  const projectId = Number.isInteger(params?.projectId) ? Number(params.projectId) : null;
  const taskOrderId = Number.isInteger(params?.taskOrderId) ? Number(params.taskOrderId) : null;

  const options =
    Number.isInteger(projectIdOrParamsOrOptions) || projectIdOrParamsOrOptions === null
      ? (maybeOptions || {})
      : (maybeOptions && Object.keys(maybeOptions).length > 0 ? maybeOptions : params);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const projectPart = Number.isInteger(taskOrderId)
    ? `?task_order_id=${taskOrderId}`
    : Number.isInteger(projectId)
      ? `?project_id=${projectId}`
      : '';

  return useQuery({
    queryKey: ['tasks', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks${projectPart}`),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useCreateTask = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/tasks', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateTask = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, payload }) => apiRequestJsonWithMethod(authenticatedFetch, `/tasks/${taskId}`, 'PUT', payload),
    onMutate: async (variables) => {
      const taskId = Number(variables?.taskId);
      const projectId = Number(variables?.projectId);
      const nextTask = variables?.payload?.task;
      const nextBudgetTarget = variables?.payload?.budgetTarget ?? variables?.payload?.budget_target;

      const hasProjectId = Number.isInteger(projectId);
      const hasTaskId = Number.isInteger(taskId);
      const hasTaskName = typeof nextTask === 'string';
      const hasBudgetTarget = nextBudgetTarget !== undefined;

      const snapshots = {
        tasks: hasProjectId ? queryClient.getQueriesData({ queryKey: ['tasks', projectId] }) : undefined,
        task: hasTaskId ? queryClient.getQueryData(['task', taskId]) : undefined,
      };

      if (hasProjectId && hasTaskId && (hasTaskName || hasBudgetTarget)) {
        queryClient.setQueriesData({ queryKey: ['tasks', projectId] }, (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((t) => {
            if (Number(t?.id) !== taskId) return t;
            const patch = {};
            if (hasTaskName) patch.task = nextTask;
            if (hasBudgetTarget) patch.budgetTarget = nextBudgetTarget === '' ? null : nextBudgetTarget;
            return { ...t, ...patch };
          });
        });
      }
      if (hasTaskId && (hasTaskName || hasBudgetTarget)) {
        queryClient.setQueryData(['task', taskId], (old) => {
          if (!old) return old;
          const patch = {};
          if (hasTaskName) patch.task = nextTask;
          if (hasBudgetTarget) patch.budgetTarget = nextBudgetTarget === '' ? null : nextBudgetTarget;
          return { ...old, ...patch };
        });
      }

      return snapshots;
    },
    onError: async (_err, variables, context) => {
      const taskId = Number(variables?.taskId);
      const projectId = Number(variables?.projectId);

      if (Number.isInteger(projectId) && Array.isArray(context?.tasks)) {
        for (const [key, data] of context.tasks) {
          queryClient.setQueryData(key, data);
        }
      }
      if (Number.isInteger(taskId) && context?.task !== undefined) {
        queryClient.setQueryData(['task', taskId], context.task);
      }
    },
    onSuccess: async (data, variables, context) => {
      const taskId = Number(variables?.taskId);
      const projectId = Number(variables?.projectId);

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      }
      if (Number.isInteger(taskId)) {
        await queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useCreateActivity = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, payload }) => apiRequestJson(authenticatedFetch, `/tasks/${taskId}/activities`, payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      // Hour edits affect project-level activity totals shown in the forecast dropdown.
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUsers = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiRequest(authenticatedFetch, '/users'),
    enabled: optionsEnabled ?? true,
    ...restOptions,
  });
};

export const useUpsertUser = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/users', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteUser = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (userId) => apiRequestWithMethod(authenticatedFetch, `/users/${userId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useTask = (taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['task', taskId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useUpdateActivity = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, payload }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}`,
        'PUT',
        payload
      ),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUser = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(userId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['user', userId],
    queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useUserRates = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(userId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['userRates', userId],
    queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}/rates`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useRates = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = projectId === null || projectId === undefined ? true : Number.isInteger(projectId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = Number.isInteger(projectId) ? `?projectId=${encodeURIComponent(String(projectId))}` : '';

  return useQuery({
    queryKey: ['rates', Number.isInteger(projectId) ? projectId : null],
    queryFn: () => apiRequest(authenticatedFetch, `/rates${qs}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useUserWorkplan = (userId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(userId) && Array.isArray(months) && months.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  const monthsParam = months.join(',');

  return useQuery({
    queryKey: ['userWorkplan', userId, monthsParam],
    queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}/workplan?months=${monthsParam}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 30000,
    ...restOptions,
  });
};

export const useMyTimeOffDays = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['myTimeOffDays'],
    queryFn: () => apiRequest(authenticatedFetch, '/connect/employeeconnect/vacation-days/me'),
    enabled: optionsEnabled ?? true,
    staleTime: 60_000,
    ...restOptions,
  });
};

export const useUserTimeOffDays = (userId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const hasUserId = Number.isInteger(userId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['userTimeOffDays', hasUserId ? userId : null],
    queryFn: () => apiRequest(authenticatedFetch, `/connect/employeeconnect/vacation-days/user/${userId}`),
    enabled: hasUserId && (optionsEnabled ?? true),
    staleTime: 60_000,
    ...restOptions,
  });
};

export const useUsersWorkplanForMonths = (userIds, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const ids = Array.isArray(userIds)
    ? userIds.map((id) => Number(id)).filter((id) => Number.isInteger(id))
    : [];
  const normalizedMonths = Array.isArray(months)
    ? months.map((m) => String(m || '').slice(0, 10)).filter(Boolean)
    : [];
  const monthsParam = normalizedMonths.join(',');

  const { enabled: optionsEnabled, ...restOptions } = options || {};
  const enabled = (optionsEnabled ?? true) && ids.length > 0 && normalizedMonths.length > 0;

  return useQueries({
    queries: ids.map((userId) => ({
      queryKey: ['userWorkplan', userId, monthsParam],
      queryFn: () => apiRequest(authenticatedFetch, `/users/${userId}/workplan?months=${monthsParam}`),
      enabled,
      staleTime: 30_000,
      ...restOptions,
    })),
  });
};

export const useUpsertUserRate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ userId, projectId, rate, effectiveDate }) =>
      apiRequestJson(authenticatedFetch, `/users/${userId}/rates`, { projectId, rate, effectiveDate }),
    onSuccess: async (data, variables, context) => {
      if (variables?.userId != null) {
        await queryClient.invalidateQueries({ queryKey: ['userRates', variables.userId] });
        await queryClient.invalidateQueries({ queryKey: ['user', variables.userId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['rates'] });

      // Rates affect forecast cost calculations across the app.
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectHourForecastForMonths'] });

      // Charts/summary views may incorporate rates.
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteUserRate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ userId, projectId, effectiveDate }) => {
      const base = `/users/${userId}/rates/${projectId}`;
      if (effectiveDate) {
        return apiRequestWithMethod(
          authenticatedFetch,
          `${base}/${encodeURIComponent(String(effectiveDate).slice(0, 10))}`,
          'DELETE'
        );
      }
      return apiRequestWithMethod(authenticatedFetch, base, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      if (variables?.userId != null) {
        await queryClient.invalidateQueries({ queryKey: ['userRates', variables.userId] });
        await queryClient.invalidateQueries({ queryKey: ['user', variables.userId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['rates'] });

      // Rates affect forecast cost calculations across the app.
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectHourForecastForMonths'] });

      // Charts/summary views may incorporate rates.
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useAddActivitySupporter = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userId }) =>
      apiRequestJson(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/supporters`, { userId }),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useSetActivitySupporters = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userIds }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/supporters`, 'PUT', {
        userIds,
      }),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteActivity = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId }) =>
      apiRequestWithMethod(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useSetActivityForecastHours = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, forecastHours }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/forecast-hours`,
        'PUT',
        { forecastHours }
      ),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useActivityHourForecast = (taskId, activityId, startMonth, count, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const startKeyRaw = String(startMonth || '').slice(0, 10);
  const startKey = startKeyRaw.length >= 7 ? `${startKeyRaw.slice(0, 7)}-01` : startKeyRaw;

  const enabled =
    Number.isInteger(taskId) &&
    Number.isInteger(activityId) &&
    typeof startMonth === 'string' &&
    startMonth.length >= 10 &&
    Number.isInteger(count);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityHourForecast', taskId, activityId, startKey, count],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast?start=${encodeURIComponent(
          startKey
        )}&count=${count}`
      ),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...restOptions,
  });
};

export const useActivityHourForecastForMonths = (taskId, activityId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityHourForecastForMonths', taskId, activityId, monthsKey],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`
      ),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...restOptions,
  });
};

export const useTaskHourForecast = (taskId, startMonth, count, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled =
    Number.isInteger(taskId) &&
    typeof startMonth === 'string' &&
    startMonth.length >= 10 &&
    Number.isInteger(count);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskHourForecast', taskId, String(startMonth).slice(0, 10), count],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/tasks/${taskId}/hour-forecast?start=${encodeURIComponent(String(startMonth).slice(0, 10))}&count=${count}`
      ),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskHourForecastForMonths = (taskId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');

  const enabled = Number.isInteger(taskId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskHourForecastForMonths', taskId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskActualsForMonths = (taskId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');
  const enabled = Number.isInteger(taskId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskActualsForMonths', taskId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/actuals?months=${encodeURIComponent(monthsKey)}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOdcMonthlyForMonths = (taskId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const normalizedMonths = Array.isArray(months)
    ? months
        .map((m) => String(m || '').slice(0, 10))
        .filter(Boolean)
        .map((s) => `${s.slice(0, 7)}-01`)
    : [];

  const monthsKey = normalizedMonths.join(',');
  const enabled = Number.isInteger(taskId) && normalizedMonths.length > 0;
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOdcMonthlyForMonths', taskId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/odc-monthly?months=${encodeURIComponent(monthsKey)}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOdcMonthlyTotals = (taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOdcMonthlyTotals', taskId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/odc-monthly-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useBulkUpsertTaskOdcMonthly = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, edits }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/odc-monthly-bulk`,
        'PUT',
        { edits }
      ),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyForMonths', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyTotals', variables.taskId] });
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOdcMonthlyTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpsertTaskActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, monthStart, rows }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/tasks/${taskId}/actuals`, 'PUT', { monthStart, rows }),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths', variables.taskId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });

      // Actual edits affect project charts.
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useClearTaskActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, monthStart }) => {
      const ms = String(monthStart || '').slice(0, 10);
      return apiRequestWithMethod(authenticatedFetch, `/tasks/${taskId}/actuals/${encodeURIComponent(ms)}`, 'DELETE');
    },
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths', variables.taskId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });

      // Clearing actuals affects project rollups and charts too.
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useActivityHourForecastMonthsWithHours = (taskId, activityId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityHourForecastMonthsWithHours', taskId, activityId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/hour-forecast-months-with-hours`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useActivityHourForecastTotals = (taskId, activityId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityHourForecastTotals', taskId, activityId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/hour-forecast-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useActivityActualsMonthlyTotals = (taskId, activityId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(taskId) && Number.isInteger(activityId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['activityActualsMonthlyTotals', taskId, activityId],
    queryFn: () => apiRequest(authenticatedFetch, `/tasks/${taskId}/activities/${activityId}/actuals-monthly-totals`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useUpsertActivityActuals = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userId, monthStart, hours }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/actuals`,
        'PUT',
        { userId, monthStart, hours }
      ),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({
          queryKey: ['activityActualsMonthlyTotals', variables.taskId, variables.activityId],
        });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityActualsMonthlyTotals'] });
      }

      // Actual edits affect project charts.
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpsertActivityHourForecast = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, userId, monthStart, hours }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast`,
        'PUT',
        { userId, monthStart, hours }
      ),
    onSuccess: async (data, variables, context) => {
      // Update any cached month windows for this activity.
      if (variables?.taskId != null && variables?.activityId != null && variables?.userId != null && variables?.monthStart) {
        const monthStart = String(variables.monthStart).slice(0, 10);
        const nextHours = Number.isFinite(Number(variables.hours)) ? Number(variables.hours) : 0;
        const userId = Number(variables.userId);
        if (Number.isInteger(userId)) {
          queryClient.setQueriesData(
            { queryKey: ['activityHourForecast', variables.taskId, variables.activityId] },
            (prev) => {
              if (!prev || typeof prev !== 'object') return prev;
              const prevRows = Array.isArray(prev.rows) ? prev.rows : [];
              const nextRows = prevRows.map((r) => {
                const rid = Number(r?.userId);
                if (!Number.isInteger(rid) || rid !== userId) return r;
                const prevHours = r?.hours && typeof r.hours === 'object' ? r.hours : {};
                return {
                  ...r,
                  hours: {
                    ...prevHours,
                    [monthStart]: nextHours,
                  },
                };
              });

              const prevTotals = prev.totals && typeof prev.totals === 'object' ? prev.totals : {};
              let nextTotalsValue = 0;
              for (const r of nextRows) {
                const hoursMap = r?.hours && typeof r.hours === 'object' ? r.hours : {};
                nextTotalsValue += Number(hoursMap?.[monthStart] ?? 0);
              }

              return {
                ...prev,
                rows: nextRows,
                totals: {
                  ...prevTotals,
                  [monthStart]: nextTotalsValue,
                },
              };
            }
          );
        }
      }

      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useBulkUpsertActivityHourForecast = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, activityId, edits }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/tasks/${taskId}/activities/${activityId}/hour-forecast-bulk`,
        'PUT',
        { edits }
      ),
    onSuccess: async (data, variables, context) => {
      if (variables?.taskId != null && variables?.activityId != null) {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast', variables.taskId, variables.activityId] });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastForMonths', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['activityHourForecastMonthsWithHours', variables.taskId, variables.activityId],
        });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals', variables.taskId, variables.activityId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
        await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      }

      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};


export const useShiftTaskHourForecast = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ taskId, deltaMonths }) =>
      apiRequestJson(authenticatedFetch, `/tasks/${taskId}/hour-forecast/shift`, { deltaMonths }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastMonthsWithHours'] });
      await queryClient.invalidateQueries({ queryKey: ['activityHourForecastTotals'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecast'] });
      await queryClient.invalidateQueries({ queryKey: ['taskHourForecastForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActivityForecastSummary'] });
      await queryClient.invalidateQueries({ queryKey: ['projectForecastedRevenue'] });

      if (variables?.taskId != null) {
        await queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
      }

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};
export const useProjectActivityForecastSummary = (projectId, rangeOrYear = null, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const range =
    typeof rangeOrYear === 'number' && Number.isInteger(rangeOrYear)
      ? { from: `${rangeOrYear}-01-01`, to: `${rangeOrYear + 1}-01-01` }
      : (rangeOrYear && typeof rangeOrYear === 'object' ? rangeOrYear : {});

  const from = typeof range?.from === 'string' ? String(range.from).slice(0, 10) : null;
  const to = typeof range?.to === 'string' ? String(range.to).slice(0, 10) : null;

  const enabled = Number.isInteger(projectId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = from && to ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : '';

  return useQuery({
    queryKey: ['projectActivityForecastSummary', projectId, from, to],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/activity-forecast-summary${qs}`),
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

const normalizeChartInterval = (interval) => {
  const v = String(interval || 'month').toLowerCase();
  if (v === 'month' || v === 'quarter' || v === 'year') return v;
  return 'month';
};

const normalizeDate10 = (raw) => {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).slice(0, 10);
  return s.length === 10 ? s : null;
};

const buildChartsQs = (interval, startDate, endDate) => {
  const params = new URLSearchParams();
  if (interval) params.set('interval', interval);
  if (startDate) params.set('start', startDate);
  if (endDate) params.set('end', endDate);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const useProjectForecastedHours = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['projectForecastedHours', projectId, safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectForecastedRevenue = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['projectForecastedRevenue', projectId, safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectHoursByUser = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['projectHoursByUser', projectId, safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/hours-by-user${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectChartDateSpan = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectChartDateSpan', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/date-span`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Number.isInteger(projectId) && (optionsEnabled ?? true),
    staleTime: 60_000,
    ...restOptions,
  });
};

const normalizeProjectIds = (projectIds) => {
  const ids = Array.isArray(projectIds) ? projectIds : [];
  return ids.map((id) => Number(id)).filter((n) => Number.isInteger(n));
};

export const useProjectsForecastedHours = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectForecastedHours', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-hours${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectsForecastedRevenue = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectForecastedRevenue', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/forecasted-revenue${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectActualRevenue = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['projectActualRevenue', projectId, safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectsActualRevenue = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectActualRevenue', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-revenue${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectInvoicedAmount = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['projectInvoicedAmount', projectId, safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/invoiced-amount${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectsInvoicedAmount = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectInvoicedAmount', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/invoiced-amount${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectsHoursByUser = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectHoursByUser', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/hours-by-user${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useAllProjectsForecastedHours = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['allProjectsForecastedHours', safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/forecasted-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsActualHours = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['allProjectsActualHours', safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/actual-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsForecastedRevenue = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['allProjectsForecastedRevenue', safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/forecasted-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsActualRevenue = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['allProjectsActualRevenue', safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/actual-revenue${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsInvoicedAmount = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['allProjectsInvoicedAmount', safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/invoiced-amount${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsHoursByUser = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['allProjectsHoursByUser', safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/hours-by-user${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsTotalBudget = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['allProjectsTotalBudget'],
    queryFn: () => apiRequest(authenticatedFetch, '/projects/charts/all/total-budget'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useAllProjectsChartsSummary = (interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['allProjectsChartsSummary', safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/charts/all/summary${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(start && end) && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useCloseDates = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['closeDates'],
    queryFn: () => apiRequest(authenticatedFetch, '/close-dates'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useCloseDateSources = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['closeDates', 'sources'],
    queryFn: () => apiRequest(authenticatedFetch, '/close-dates/sources'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useActiveCloseDateSource = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['closeDates', 'source'],
    queryFn: () => apiRequest(authenticatedFetch, '/close-dates/source'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useSetActiveCloseDateSource = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ sourceName }) =>
      apiRequestJsonWithMethod(authenticatedFetch, '/close-dates/source', 'PUT', { sourceName }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
      await queryClient.invalidateQueries({ queryKey: ['closeDates', 'source'] });
      await queryClient.invalidateQueries({ queryKey: ['closeDates', 'sources'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpsertCloseDate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (payload) => apiRequestJson(authenticatedFetch, '/close-dates', payload),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteCloseDate = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (id) => apiRequestWithMethod(authenticatedFetch, `/close-dates/${id}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['closeDates'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProviders = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['providers'],
    queryFn: () => apiRequest(authenticatedFetch, '/providers'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useActiveProvider = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['providers', 'active'],
    queryFn: () => apiRequest(authenticatedFetch, '/providers/active'),
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};

export const useSetActiveProvider = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ providerName, config }) =>
      apiRequestJsonWithMethod(authenticatedFetch, '/providers/active', 'PUT', { providerName, config }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['providers', 'active'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateProviderConfig = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ providerName, config }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/providers/${providerName}/config`, 'PUT', { config }),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['providers', 'active'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, payload }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/projects/${projectId}`, 'PUT', payload),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);

      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestWithMethod(authenticatedFetch, `/projects/${projectId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables);
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['trashedProjects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectActualHours', projectId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useRestoreProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestJson(authenticatedFetch, `/projects/${projectId}/restore`, {}),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['trashedProjects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const usePermanentDeleteProject = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (projectId) => apiRequestWithMethod(authenticatedFetch, `/projects/${projectId}/permanent`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables);
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({ queryKey: ['trashedProjects'] });
      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['tasks', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
        await queryClient.invalidateQueries({ queryKey: ['projectActualHours', projectId] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProjectUsers = (projectId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const enabled = Number.isInteger(projectId);
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectUsers', projectId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/users`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useSetProjectUsers = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, userIds }) =>
      apiRequestJsonWithMethod(authenticatedFetch, `/projects/${projectId}/users`, 'PUT', { userIds }),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      await queryClient.invalidateQueries({ queryKey: ['projectUsers', projectId] });
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProjectActualHours = (projectId, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const enabled = Number.isInteger(projectId);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQuery({
    queryKey: ['projectActualHours', projectId, safeInterval, start, end],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-hours${qs}`),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: enabled && (optionsEnabled ?? true),
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectsActualHours = (projectIds, interval, startDate, endDate, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();

  const safeInterval = normalizeChartInterval(interval);
  const start = normalizeDate10(startDate);
  const end = normalizeDate10(endDate);
  const { enabled: optionsEnabled, ...restOptions } = options;

  const ids = normalizeProjectIds(projectIds);
  const enabled = ids.length > 0 && Boolean(start && end);
  const qs = buildChartsQs(safeInterval, start, end);

  return useQueries({
    queries: ids.map((projectId) => ({
      queryKey: ['projectActualHours', projectId, safeInterval, start, end],
      queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/charts/actual-hours${qs}`),
      ...DEFAULT_QUERY_OPTIONS,
      enabled: enabled && (optionsEnabled ?? true),
      staleTime: 0,
      ...restOptions,
    })),
  });
};

export const useProjectHourForecastForMonths = (projectId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectHourForecastForMonths', projectId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderHourForecastForMonths = (projectId, taskOrderId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderHourForecastForMonths', projectId, taskOrderId, monthsKey],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/hour-forecast?months=${encodeURIComponent(monthsKey)}`
      ),
    enabled:
      (optionsEnabled ?? true) &&
      Number.isInteger(projectId) &&
      Number.isInteger(taskOrderId) &&
      !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useProjectActualsForMonths = (projectId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['projectActualsForMonths', projectId, monthsKey],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/actuals?months=${encodeURIComponent(monthsKey)}`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderActualsForMonths = (projectId, taskOrderId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderActualsForMonths', projectId, taskOrderId, monthsKey],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/actuals?months=${encodeURIComponent(monthsKey)}`
      ),
    enabled:
      (optionsEnabled ?? true) &&
      Number.isInteger(projectId) &&
      Number.isInteger(taskOrderId) &&
      !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderMonthTaskRollup = (projectId, taskOrderId, monthStart, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const ms = typeof monthStart === 'string' ? monthStart.slice(0, 10) : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderMonthTaskRollup', projectId, taskOrderId, ms],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/month-task-rollup?month=${encodeURIComponent(ms)}`
      ),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId) && !!ms,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderTaskMonthRollup = (projectId, taskOrderId, months, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const monthsKey = Array.isArray(months) ? months.filter(Boolean).join(',') : '';
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderTaskMonthRollup', projectId, taskOrderId, monthsKey],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/task-month-rollup?months=${encodeURIComponent(monthsKey)}`
      ),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId) && !!monthsKey,
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderActualDollarsByTask = (projectId, taskOrderId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderActualDollarsByTask', projectId, taskOrderId],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/actual-dollars-by-task`
      ),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId),
    staleTime: 0,
    ...restOptions,
  });
};

export const useTaskOrderTaskSpendingByMonth = (projectId, taskOrderId, taskId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderTaskSpendingByMonth', projectId, taskOrderId, taskId],
    queryFn: () =>
      apiRequest(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}/spending-by-month`
      ),
    enabled:
      (optionsEnabled ?? true)
      && Number.isInteger(projectId)
      && Number.isInteger(taskOrderId)
      && Number.isInteger(taskId),
    staleTime: 5 * 60 * 1000,
    ...restOptions,
  });
};

export const useTaskOrderTasksSpendingByMonth = (projectId, taskOrderId, taskIds, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const ids = Array.isArray(taskIds)
    ? taskIds.map((id) => Number(id)).filter((id) => Number.isInteger(id))
    : [];
  const { enabled: optionsEnabled, ...restOptions } = options;
  const enabledBase =
    (optionsEnabled ?? true)
    && Number.isInteger(projectId)
    && Number.isInteger(taskOrderId)
    && ids.length > 0;

  return useQueries({
    queries: ids.map((taskId) => ({
      queryKey: ['taskOrderTaskSpendingByMonth', projectId, taskOrderId, taskId],
      queryFn: () =>
        apiRequest(
          authenticatedFetch,
          `/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}/spending-by-month`
        ),
      enabled: enabledBase,
      staleTime: 5 * 60 * 1000,
      ...restOptions,
    })),
  });
};

export const useTaskOrderInvoices = (projectId, taskOrderId, options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['taskOrderInvoices', projectId, taskOrderId],
    queryFn: () => apiRequest(authenticatedFetch, `/projects/${projectId}/task-orders/${taskOrderId}/invoices`),
    enabled: (optionsEnabled ?? true) && Number.isInteger(projectId) && Number.isInteger(taskOrderId),
    staleTime: 0,
    ...restOptions,
  });
};

export const useCreateTaskOrderInvoice = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, payload }) =>
      apiRequestJson(authenticatedFetch, `/projects/${projectId}/task-orders/${taskOrderId}/invoices`, payload),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount'] });
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteTaskOrderInvoice = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, invoiceId }) =>
      apiRequestWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/invoices/${invoiceId}`,
        'DELETE'
      ),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount'] });
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUpdateTaskOrderInvoice = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, invoiceId, payload }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/invoices/${invoiceId}`,
        'PUT',
        payload
      ),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      if (Number.isInteger(projectId)) {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount', projectId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['projectInvoicedAmount'] });
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useUploadTaskOrderInvoicePdf = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, invoiceId, payload }) =>
      apiRequestJsonWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/invoices/${invoiceId}/pdf`,
        'POST',
        payload
      ),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteTaskOrderInvoicePdf = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, invoiceId }) =>
      apiRequestWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/invoices/${invoiceId}/pdf`,
        'DELETE'
      ),
    onSuccess: async (data, variables, context) => {
      const projectId = Number(variables?.projectId);
      const taskOrderId = Number(variables?.taskOrderId);

      if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices', projectId, taskOrderId] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ['taskOrderInvoices'] });
      }

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useClearProjectActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, monthStart }) => {
      const ms = String(monthStart || '').slice(0, 10);
      return apiRequestWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/actuals/${encodeURIComponent(ms)}`,
        'DELETE'
      );
    },
    onSuccess: async (data, variables, context) => {
      if (variables?.projectId != null) {
        await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths', variables.projectId] });
      }
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });

      // Clearing actuals affects task-level actuals queries and project charts.
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useClearTaskOrderActualsForMonth = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ projectId, taskOrderId, monthStart }) => {
      const ms = String(monthStart || '').slice(0, 10);
      return apiRequestWithMethod(
        authenticatedFetch,
        `/projects/${projectId}/task-orders/${taskOrderId}/actuals/${encodeURIComponent(ms)}`,
        'DELETE'
      );
    },
    onSuccess: async (data, variables, context) => {
      if (variables?.projectId != null && variables?.taskOrderId != null) {
        await queryClient.invalidateQueries({
          queryKey: ['taskOrderActualsForMonths', variables.projectId, variables.taskOrderId],
        });
        await queryClient.invalidateQueries({
          queryKey: ['taskOrderHourForecastForMonths', variables.projectId, variables.taskOrderId],
        });
      }

      await queryClient.invalidateQueries({ queryKey: ['taskOrderActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['taskOrderHourForecastForMonths'] });

      // Keep older query keys reasonably fresh if they are still used anywhere.
      await queryClient.invalidateQueries({ queryKey: ['projectActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectHourForecastForMonths'] });

      // Clearing actuals affects task-level actuals queries and project charts.
      await queryClient.invalidateQueries({ queryKey: ['taskActualsForMonths'] });
      await queryClient.invalidateQueries({ queryKey: ['projectActualHours'] });
      await queryClient.invalidateQueries({ queryKey: ['projectsActualHours'] });

      await invalidateTaskOrderSpendingQueries(queryClient);

      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useDeleteTask = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: (taskId) => apiRequestWithMethod(authenticatedFetch, `/tasks/${taskId}`, 'DELETE'),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (variables != null) {
        await queryClient.removeQueries({ queryKey: ['task', variables] });
      }
      return onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export const useProjectsMilestones = (options = {}) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const { enabled: optionsEnabled, ...restOptions } = options;

  return useQuery({
    queryKey: ['milestones', 'allProjects'],
    queryFn: () => apiRequest(authenticatedFetch, '/projects/milestones'),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: optionsEnabled ?? true,
    staleTime: 0,
    ...restOptions,
  });
};